import axios from 'axios';
import * as R from 'ramda';
import { message } from 'antd';
import { normalize } from 'normalizr';
import { batchActions } from 'redux-batched-actions';

import { getResErrorMsg } from '../../utils';
import * as schemes from '../../api/schemes';
import * as ApikoApi from '../../api/apiko-api';
import * as actions from '../actions/companies';
import { fetchTasksSuccess } from '../actions/tasks';
import { fetchCommentsSuccess } from '../actions/comments';
import { fetchRequestsSuccess } from '../actions/requests';
import { fetchContactsSuccess } from '../actions/contacts';
import { fetchAttachmentsSuccess } from '../actions/attachments';
import { fetchRequestLogsSuccess } from '../actions/request-logs';
import { fetchMeetingNotesSuccess } from '../actions/meeting-notes';

const getCurrentPage = ({ companies: { page } }) => page;
const getNextPage = ({ companies: { page } }) => page + 1;

const getCompaniesFilters = ({
  companies: {
    filters: {
      countries,
    },
  },
}) => ({
  countries: countries[0] && countries.join(),
});


/**
 * fetchCompanyById :: (Object, ID, Boolean) -> Func -> Promise Error _
 */
export const fetchCompanyById = (cancelToken, companyId, polling = false) => dispatch =>
  Promise.resolve(!polling && dispatch(actions.fetchCompaniesRequest()))
    .then(() => ApikoApi.fetchCompanyById(companyId, cancelToken))
    .then(({ data, responseTime }) => {
      const { result, entities } = normalize(data, schemes.company);

      dispatch(batchActions([
        actions.fetchCompaniesSuccess({
          ids: result ? [result] : [],
          notNewPage: true,
          existsMore: false,
          companies: entities.companies || {},
        }),
        fetchRequestsSuccess({
          requests: entities.requests || {},
          notNewPage: true,
        }),
        fetchContactsSuccess({
          contacts: entities.contacts || {},
          notNewPage: true,
        }),
        fetchMeetingNotesSuccess({
          meetingNotes: entities.meetingNotes || {},
          notNewPage: true,
        }),
        fetchCommentsSuccess(entities.comments),
        fetchRequestLogsSuccess({
          logs: entities.logs || {},
        }),
        fetchTasksSuccess({
          tasks: entities.tasks || {},
          notNewPage: true,
        }),
        ...(!R.isEmpty(entities.attachments)
          ? [fetchAttachmentsSuccess(entities.attachments)]
          : []),
      ]));

      return responseTime;
    })
    .catch((e) => {
      if (axios.isCancel(e)) return;

      if (!polling) {
        dispatch(actions.fetchCompaniesFailure());

        throw new Error();
      }
    });


/**
 * fetchCompanies :: (Object, Boolean) -> (Func, Func) -> Promise Error _
 */
export const fetchCompanies = (cancelToken, polling = false) => (dispatch, getState) =>
  Promise.resolve(!polling && dispatch(actions.fetchCompaniesRequest()))
    .then(() => ApikoApi.fetchCompanies({
      page: polling ? getCurrentPage(getState()) : getNextPage(getState()),
      ...getCompaniesFilters(getState()),
      cancelToken,
      polling,
    }))
    .then(({ data, responseTime }) => {
      const { result, entities } = normalize(
        data,
        schemes.companies,
      );

      dispatch(actions.fetchCompaniesSuccess({
        polling,
        count: result.count,
        ids: result.companies,
        companies: entities.companies,
        existsMore: result.existsMore,
        ...(polling ? { notNewPage: true } : {}),
      }));

      return responseTime;
    })
    .catch((e) => {
      if (axios.isCancel(e)) return;

      if (!polling) dispatch(actions.fetchCompaniesFailure());
    });


/**
 * searchCompanies :: (String, Object, Boolean) -> (Func, Func) -> Promise Error
 */
export const searchCompanies = (searchTerm, cancelToken, polling = false) => (dispatch, getState) =>
  Promise.resolve(!polling && dispatch(actions.searchCompaniesRequest()))
    .then(() => ApikoApi.fetchCompanies({
      page: polling ? getCurrentPage(getState()) : getNextPage(getState()),
      ...getCompaniesFilters(getState()),
      searchTerm,
      cancelToken,
      polling,
    }))
    .then(({ data, responseTime }) => {
      const { result, entities } = normalize(
        data,
        schemes.companies,
      );

      dispatch(actions.searchCompaniesSuccess({
        polling,
        count: result.count,
        ids: result.companies,
        companies: entities.companies,
        existsMore: result.existsMore,
        ...(polling ? { notNewPage: true } : {}),
      }));

      return responseTime;
    })
    .catch((e) => {
      if (axios.isCancel(e)) return;

      if (!polling) dispatch(actions.searchCompaniesFailure());
    });


/**
 * handleCompanyCreate :: Object -> Func -> Promise Error _
 */
export const handleCompanyCreate = data => dispatch =>
  Promise.resolve(dispatch(actions.createCompanyRequest()))
    .then(() => ApikoApi.createCompany(data))
    .then(res => dispatch(actions.createCompanySuccess(res)));


/**
 * handleCompanyUpdate :: (String, Object) -> Func -> Promise Error _
 */
export const handleCompanyUpdate = (companyId, data) => dispatch =>
  Promise.resolve(dispatch(actions.updateCompanyRequest()))
    .then(() => ApikoApi.updateCompanyById(companyId, data))
    .then(res => dispatch(actions.updateCompanySuccess(res)));


/**
 * handleCompanyDelete :: ID -> Func -> Promise Error _
 */
export const handleCompanyDelete = contactId => dispatch =>
  Promise.resolve(dispatch(actions.deleteCompanyRequest()))
    .then(() => ApikoApi.deleteCompanyById(contactId))
    .then(() => dispatch(actions.deleteCompanySuccess(contactId)))
    .then(() => message.success('Company successfully removed'))
    .catch((err) => {
      message.error(`${getResErrorMsg(err)}`);

      return dispatch(actions.deleteCompanyFailure());
    });



// WEBPACK FOOTER //
// ./src/redux/thunk/companies.js